// Plain-text formatting helpers for the projection transcript. Currency/percent
// delegate to the shared app-wide formatters so the transcript can't drift from
// the rest of the app. Fixed-width tables are deliberate: wide LTC rows read far
// better as aligned columns than as markdown pipes or JSON.

import { formatCurrency } from '../../../../formatCurrency';

// Whole-dollar currency, matching the on-page tables (`$720,000`).
export function currency(value: number): string {
  return formatCurrency(value, 0);
}

// Cents-precision currency for daily-benefit rates (`$200.00`).
export function currencyCents(value: number): string {
  return formatCurrency(value, 2);
}

// A signed delta rendered for the running-balance columns. Zero collapses to an
// em dash so the eye skips unchanged rows; non-zero uses a true minus sign and
// no currency symbol to stay compact (`(−6,300)`, `(+1,200)`).
export function delta(value: number): string {
  const rounded = Math.round(value);
  // Collapse to em dash when the rounded change is zero (not just exact 0) so a
  // sub-rounding value like -0.3 doesn't render as "(−0)".
  if (rounded === 0) return '(—)';
  const sign = rounded > 0 ? '+' : '−';
  const magnitude = Math.abs(rounded).toLocaleString('en-US');
  return `(${sign}${magnitude})`;
}

// A decimal rate (0.05) rendered as a percent string (`5.0%`). Unlike the
// app-wide `formatPercent` (which strips trailing zeros), the transcript keeps
// exactly one decimal place so a column of rates aligns visually.
export function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

export interface TranscriptColumn {
  header: string;
  align?: 'left' | 'right';
}

// Render a fixed-width table: a header row followed by data rows, every column
// padded to the widest cell (header included). Left-aligned columns pad on the
// right; right-aligned pad on the left. Cells are separated by two spaces and
// trailing whitespace is trimmed per line.
export function fixedWidthTable(columns: TranscriptColumn[], rows: string[][]): string {
  const widths = columns.map((col, i) =>
    Math.max(col.header.length, ...rows.map(row => (row[i] ?? '').length)),
  );

  const renderRow = (cells: string[]): string =>
    columns
      .map((col, i) => {
        const cell = cells[i] ?? '';
        const width = widths[i];
        return col.align === 'right' ? cell.padStart(width) : cell.padEnd(width);
      })
      .join('  ')
      .replace(/\s+$/, '');

  return [renderRow(columns.map(c => c.header)), ...rows.map(renderRow)].join('\n');
}

// Two-column "label: value" block with the values right-aligned into a column,
// used for the returns summary. `label` is padded so the colon-separated values
// line up.
export function labelledAmounts(rows: Array<{ label: string; value: string }>): string {
  const labelWidth = Math.max(...rows.map(r => r.label.length)) + 1; // + colon
  const valueWidth = Math.max(...rows.map(r => r.value.length));
  return rows
    .map(r => `${`${r.label}:`.padEnd(labelWidth)}  ${r.value.padStart(valueWidth)}`)
    .join('\n');
}
